'use client';

import { Trans, Locale } from '@tuic/i18n';
import { createElement, FC, useEffect, useMemo, useState } from 'react';
import Script from 'next/script';
import { useClientTranslation } from '@lib/i18n/client';
import { LoadingSpinner } from '@tuic/web-compositions/core/LoadingSpinner';
import { DashboardError } from '@tuic/web-compositions/my-trips/DashboardError';

type SurveyContentProps = {
  locale: Locale;
  surveyId: number;
  userAge?: number;
  apiToken: string;
};

type ScriptState = 'loading' | 'loaded' | 'error';

export const SurveyContent: FC<SurveyContentProps> = ({
  locale,
  surveyId,
  userAge,
  apiToken,
}) => {
  const { t } = useClientTranslation();

  // Convert locale to survey format
  const surveyLocale = locale === 'de' ? 'de-DE' : 'en-US';

  const [stateWebComponents, setStateWebComponents] =
    useState<ScriptState>('loading');
  const [stateCustomElements, setStateCustomElements] =
    useState<ScriptState>('loading');
  const [stateSurvey, setStateSurvey] = useState<ScriptState>('loading');

  const scripts = useMemo(
    () => ({
      webcomponents: '/api/survey/js/webcomponents-bundle.js',
      customElements: '/api/survey/js/custom-elements-es5-adapter.js',
      survey: '/api/survey/tuic-survey.js',
    }),
    [],
  );

  let content = (
    <div className="text-center mt-4">
      {t('Fragebogen wird geladen...')}
      <LoadingSpinner className="m-auto w-16 h-16" size="md" />
    </div>
  );
  const hasErrorStates = !![
    stateWebComponents,
    stateCustomElements,
    stateSurvey,
  ].find(state => state === 'error');
  const hasLoadingStates = !![
    stateWebComponents,
    stateCustomElements,
    stateSurvey,
  ].find(state => state === 'loading');

  if (hasErrorStates && !hasLoadingStates) {
    // At least one script failed to load
    content = (
      <div className="mt-4 mx-2">
        <DashboardError
          headline={t('Leider gibt es aktuell technische Probleme.')}
          subline={
            <Trans>
              Leider haben wir aktuell technische Probleme. Bitte versuchen Sie
              es in ein paar Minuten noch einmal.
              <br />
              Wir bedauern diesen Zustand und danken für Ihr Verständnis!
            </Trans>
          }
          signature={t('Ihr Mein Schiff Team')}
          cta={{
            asChild: true,
            children: (
              <button
                onClick={() => {
                  // Force reload the page
                  window.location.reload();
                }}
              >
                {t('Erneut versuchen')}
              </button>
            ),
          }}
        />
      </div>
    );
  } else if (!hasLoadingStates) {
    // All scripts are loaded yet
    content = createElement('tuic-survey', {
      'api-base-url': '/api/survey/',
      locale: surveyLocale,
      'survey-id': surveyId,
      'user-age': userAge,
      'api-token': apiToken,
    });
  }

  return (
    <>
      <SurveyScript
        key={'survey-script-webcomponents'}
        src={`${scripts.webcomponents}`}
        state={stateWebComponents}
        onLoaded={() => setStateWebComponents('loaded')}
        onError={() => setStateWebComponents('error')}
      />
      <SurveyScript
        key={'survey-script-custom-elements'}
        src={`${scripts.customElements}`}
        state={stateCustomElements}
        onLoaded={() => setStateCustomElements('loaded')}
        onError={() => setStateCustomElements('error')}
      />
      {stateWebComponents !== 'loading' &&
        stateCustomElements !== 'loading' && (
          <SurveyScript
            key={'survey-script-survey'}
            src={`${scripts.survey}`}
            state={stateSurvey}
            onLoaded={() => setStateSurvey('loaded')}
            onError={() => setStateSurvey('error')}
          />
        )}
      {content}
    </>
  );
};

type SurveyScriptProps = {
  src: string;
  state: ScriptState;
  onLoaded: () => void;
  onError: () => void;
};

const SurveyScript = ({ src, state, onLoaded, onError }: SurveyScriptProps) => {
  const [onLoadDone, setOnLoadDone] = useState(false);
  const [onReadyDone, setOnReadyDone] = useState(false);
  const [onErrorDone, setOnErrorDone] = useState(false);

  useEffect(() => {
    if (state === 'loaded') {
      // Already loaded
      return;
    }

    if (onErrorDone) {
      // Error occurred
      return onError();
    }

    if (onLoadDone && onReadyDone) {
      // Loaded
      return onLoaded();
    }

    // Timeout for loading
    const timeoutId = setTimeout(() => {
      onError();
    }, 60000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [onLoadDone, onReadyDone, onErrorDone, onError, onLoaded, state]);

  return (
    <Script
      src={src}
      type="text/javascript"
      async
      // Remove the event handlers when the script is already loaded
      onLoad={state === 'loaded' ? () => {} : () => setOnLoadDone(true)}
      onReady={state === 'loaded' ? () => {} : () => setOnReadyDone(true)}
      onError={state === 'loaded' ? () => {} : () => setOnErrorDone(true)}
    />
  );
};
